import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState, useRef } from 'react'

import { useQuinceRouter } from '@src/modules/navigation'
import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import CollectionFilterLoader from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilterLoader'
import { CheckboxGroup, ColorFilterGroup, DropdownV2, Icon, RadioButtonGroup } from '@src/quince-components/src/atoms'
import StickyWrapper from '@src/quince-components/src/atoms/stickyWrapper'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

import { FilterCollapseGroup } from '../../components/FilterCollapseGroup'
import { FilterDrawer } from '../../components/FilterDrawer'
import { FilterDropdown } from '../../components/FilterDropdown'
import { FILTER_DROPDOWN_TYPES, MAX_FILTERS, SORT_OPTIONS_CLP_EXPERIMENT } from '../../constants/constants'
import {
  CLEAR_ALL_THRESHOLD,
  COLORS_FILTER_KEY,
  FALLBACK_COLOR,
  FILTER_TYPES,
  GLOBAL_FILTER_KEY,
} from '../../constants/filterSection.constants'
import { filterListOnExp, useShouldHideDeptAndCategoryFilter } from '../../filterListOnExp'
import { useEnhancedFilterState } from '../../hooks/useEnhancedFilterState'
import { CheckboxOption, FilterItem, FilterSectionWithDrawerProps } from '../filterSection.interface'
import {
  toTitleCase,
  getSelectedCount,
  createCheckboxOptions,
  excludeDepartmentFilter,
  handleUrlBasedProductTypeSelection,
  handleRadioFilterChange,
} from '../filterUtils'
import FilterInlineIcon from '../icon/filter.inline.svg'

import styles from './filterSectionWithDrawer.module.scss'

// SessionStorage constants
const FILTER_DRAWER_STORAGE_KEY = 'collection-filter-drawer'
const FILTER_COLLAPSE_STATE_KEY = 'collection-filter-collapse-state'

export const FilterSectionWithDrawer: React.FC<FilterSectionWithDrawerProps> = props => {
  const {
    className,
    filters: serverFilters = [],
    noOfItems = 0,
    selectedFilters = {},
    sortBy = '',
    hideFilters = false,
    skipSorting = false,
    collectionSlug,
    updateQuery,
    showShimmer,
    showGroupedPage = false,
  } = props

  const router = useQuinceRouter()
  const pathname = router.pathname
  const routerAsPath = router.asPath.split('?')[0] ?? ''

  // Initialize drawer state based on session storage immediately
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') return false

      const sessionStorage = getSessionStorage()
      const storedPath = sessionStorage.getItem<string>(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      return storedPath === pathname
    } catch (error) {
      console.error('Error reading initial filter drawer state:', error)
      return false
    }
  })

  // Initialize collapse state based on session storage
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>(() => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') return []

      const sessionStorage = getSessionStorage()
      const storedCollapseState = sessionStorage.getItem<string>(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
      return storedCollapseState ? JSON.parse(storedCollapseState) : []
    } catch (error) {
      console.error('Error reading initial collapse state:', error)
      return []
    }
  })

  // Add state for tracking sticky status and refs for open dropdowns
  const [isSticky, setIsSticky] = useState(false)
  const openDropdownRefs = useRef<Set<string>>(new Set())

  // Add state to track initial sticky change
  const [hasHandledStickyChange, setHasHandledStickyChange] = useState(false)

  // Add function to close all dropdowns
  const closeAllDropdowns = () => {
    openDropdownRefs.current.clear()
    // Force re-render of dropdown components
    setIsSticky(prev => prev)
  }

  // Use the enhanced filter state hook
  const {
    selectedFiltersState,
    selectedFilterChips,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
    effectiveTotalSelectedFilters,
  } = useEnhancedFilterState({
    selectedFilters,
    showGroupedPage,
    routerAsPath,
    collectionSlug,
    serverFilters,
    updateQuery,
  })

  // Translation hook
  const { t } = useTranslation('common')

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    )
      .filter(item => item.values.length > 1)
      .slice(0, MAX_FILTERS)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  // Modified effect to handle updates without causing flicker
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    try {
      const sessionStorage = getSessionStorage()
      const storedPath = sessionStorage.getItem<string>(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })

      if (storedPath === pathname) {
        if (!isDrawerOpen) {
          setIsDrawerOpen(true)
          // Set all filter items to be expanded by default
          const newCollapseKeys = filters.map(filter => filter.name)
          setCollapseActiveKeys(newCollapseKeys)
          // Store collapse state
          sessionStorage.setItem(FILTER_COLLAPSE_STATE_KEY, JSON.stringify(newCollapseKeys), { isGlobal: true })
        }
      } else {
        if (storedPath) {
          // Path doesn't match, clear the stored values and close drawer
          sessionStorage.removeItem(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
          sessionStorage.removeItem(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
        }
        if (isDrawerOpen) {
          setIsDrawerOpen(false)
          setCollapseActiveKeys([])
        }
      }
    } catch (error) {
      // Handle any sessionStorage errors gracefully
      console.error('Error reading filter drawer state from sessionStorage:', error)
      // Clear any potentially corrupted data and close drawer
      try {
        const sessionStorage = getSessionStorage()
        sessionStorage.removeItem(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
        sessionStorage.removeItem(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
      } catch (clearError) {
        console.error('Error clearing filter drawer state from sessionStorage:', clearError)
      }
      if (isDrawerOpen) {
        setIsDrawerOpen(false)
        setCollapseActiveKeys([])
      }
    }
  }, [pathname, filters, isDrawerOpen])

  // Add effect to persist collapse state changes
  useEffect(() => {
    if (typeof window === 'undefined' || !isDrawerOpen) return

    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.setItem(FILTER_COLLAPSE_STATE_KEY, JSON.stringify(collapseActiveKeys), { isGlobal: true })
    } catch (error) {
      console.error('Error storing collapse state:', error)
    }
  }, [collapseActiveKeys, isDrawerOpen])

  const shouldHideFilters = hideFilters || filters.length === 0

  // Handle sort change
  const handleSortChange = (value: string) => {
    updateQuery({ sort: { type: 'toggle', value } })
  }

  // Modify renderSortDropdown to include forceClose prop
  const renderSortDropdown = () => {
    if (skipSorting) return null

    const selectedSortOption = SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.SORT}
        label={
          <span>
            {t('sort_by')}
            {`: ${t(selectedSortOption?.labelKey ?? '')}`}
          </span>
        }
        value={sortBy}
        onChange={handleSortChange}
        placement="bottomRight"
        menuClassName={styles.sortDropdownMenu}
        buttonClassName={styles.sortDropdown}
        isMobile={false}
        isActive={sortBy !== ''}
        onTriggerClick={(_, isOpen) => {
          if (isOpen) {
            trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
          }
        }}
        // eslint-disable-next-line i18next/no-literal-string
        dropdownKey="filter-section-drawer-sort"
        forceClose={isSticky}
      />
    )
  }

  // If we should hide all (filters and sorting)
  if (shouldHideFilters && skipSorting) {
    return null
  }

  // If we should hide filters but sorting is enabled, only render the sort functionality
  if (shouldHideFilters) {
    return (
      <div className={classNames(styles.filtersContainer, className)}>
        <div className={styles.filtersWrapper}>
          <div className={styles.filtersLeft}></div>
          <div className={styles.filtersRight}>{renderSortDropdown()}</div>
        </div>
      </div>
    )
  }

  // Enhanced drawer control with direct filter access
  const handleMainFilterClick = () => {
    // Set all filter items to be expanded by default
    const newCollapseKeys = filters.map(filter => filter.name)
    setCollapseActiveKeys(newCollapseKeys)
    setIsDrawerOpen(true)

    // Store current path and collapse state in sessionStorage
    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.setItem(FILTER_DRAWER_STORAGE_KEY, pathname, { isGlobal: true })
      sessionStorage.setItem(FILTER_COLLAPSE_STATE_KEY, JSON.stringify(newCollapseKeys), { isGlobal: true })
    } catch (error) {
      console.error('Error storing filter drawer state in sessionStorage:', error)
    }

    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: GLOBAL_FILTER_KEY,
    })
  }

  const handleFilterDropdownTriggerClick = (filterKey: string, isOpen: boolean) => {
    if (isOpen) {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setCollapseActiveKeys([])

    // Remove current path and collapse state from sessionStorage
    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.removeItem(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      sessionStorage.removeItem(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
    } catch (error) {
      console.error('Error removing filter drawer state from sessionStorage:', error)
    }
  }

  // Helper function to render a checkbox filter (common between inline and drawer)
  const renderCheckboxFilter = (
    filter: FilterItem,
    options: CheckboxOption[],
    onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
    forceClose = false,
  ) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState)
    const selectedOptions = options.filter(option => option.checked)

    if (isDrawer) {
      return (
        <CheckboxGroup
          key={`drawer-${filter.name}-${selectedCount}`}
          options={options}
          onChange={onChange}
          direction="column"
          maxVisible={8}
          className={styles.checkboxGroup}
          showMoreButtonClassName={styles.showMoreButtonDrawer}
        />
      )
    }

    // Format the label for the checkbox filter
    const formatCheckboxFilterLabel = () => {
      const singleSelectedValue =
        selectedCount === 1 && selectedOptions.length === 1 ? selectedOptions[0]?.label : undefined

      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <DropdownV2
        key={`${filter.name}-filter`}
        menu={{
          items: [
            {
              key: `${filter.name}-filter`,
              label: (
                <CheckboxGroup
                  key={`checkbox-${selectedCount}`}
                  options={options}
                  onChange={onChange}
                  direction="column"
                  maxVisible={8}
                  className={styles.checkboxPadding}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        multiSelect={true}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatCheckboxFilterLabel()}</span>
      </DropdownV2>
    )
  }

  // Helper function to render a color filter (common between inline and drawer)
  const renderColorFilter = (filter: FilterItem, isDrawer = false, forceClose = false) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState)

    const colorOptions = filter.values.map(value => {
      if (typeof value === 'string') {
        return {
          value,
          hex: FALLBACK_COLOR,
          label: value,
        }
      }

      const colorValue = value
      return {
        value: colorValue.name,
        hex: colorValue.hex ?? FALLBACK_COLOR,
        label: colorValue.displayName ?? colorValue.name,
        imgURL: colorValue.imgURL ?? undefined,
      }
    })

    const handleColorChangeForFilter = (value: string) => {
      handleColorChange(filter, value)
    }

    if (isDrawer) {
      return (
        <ColorFilterGroup
          key={`drawer-color-group-${selectedFiltersState[filter.name]?.length}`}
          options={colorOptions}
          selectedValues={selectedFiltersState[filter.name] ?? []}
          onChange={handleColorChangeForFilter}
          direction="horizontal"
          className={styles.colorFilterGroupDrawer}
          columns={5}
          circleSize={2.2}
          rowGap={1.6}
          columnGap={0}
        />
      )
    }

    // Format the label for the color filter
    const formatColorFilterLabel = () => {
      const singleSelectedValue =
        selectedFiltersState[filter.name]?.length === 1 ? selectedFiltersState[filter.name]?.[0] : undefined
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <DropdownV2
        key={`${filter.name}-filter`}
        menu={{
          items: [
            {
              key: COLORS_FILTER_KEY,
              label: (
                <ColorFilterGroup
                  key={`color-group-${selectedFiltersState[filter.name]?.length}`}
                  options={colorOptions}
                  selectedValues={selectedFiltersState[filter.name] ?? []}
                  onChange={handleColorChangeForFilter}
                  direction="horizontal"
                  circleClassName={styles.colorFilterCircle}
                  className={styles.colorFilterGroup}
                  columns={3}
                  circleSize={3.6}
                  rowGap={2}
                  columnGap="auto"
                />
              ),
            },
          ],
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        multiSelect={true}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatColorFilterLabel()}</span>
      </DropdownV2>
    )
  }

  // Add renderRadioFilter function
  const renderRadioFilter = (
    filter: FilterItem,
    options: CheckboxOption[],
    _onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
    forceClose = false,
  ) => {
    const filterName = filter.name === EFilterKeys.PRODUCT_TYPE ? EFilterKeys.SUBCOLLECTIONS : filter.name
    const mappedFilter = filterName !== filter.name ? { ...filter, name: filterName } : filter

    // For grouped pages, use URL-based selection for product type filter
    let currentValue = selectedFiltersState[filterName]?.[0] ?? ''
    let hasUrlBasedSelection = false

    if (filter.name === EFilterKeys.PRODUCT_TYPE && showGroupedPage) {
      const { currentValue: urlBasedValue, hasUrlMatch } = handleUrlBasedProductTypeSelection(
        filter,
        options,
        routerAsPath,
        selectedFiltersState,
      )
      currentValue = urlBasedValue
      hasUrlBasedSelection = hasUrlMatch
    }

    // Calculate selectedCount - use URL-based selection for grouped pages if available
    const selectedCount = hasUrlBasedSelection ? 1 : getSelectedCount(mappedFilter, selectedFiltersState)
    const selectedOptions = options.filter(option => option.checked)

    const handleRadioChange = (value: string) => {
      handleRadioFilterChange(value, options, filter, showGroupedPage, router, routerAsPath, updateQuery, filters)
    }

    if (isDrawer) {
      return (
        <RadioButtonGroup
          key={`drawer-${filterName}-${selectedCount}`}
          options={options.map(opt => ({
            label: opt.label,
            value: opt.id,
            checked: opt.checked,
          }))}
          onChange={handleRadioChange}
          name={filterName}
          className={styles.radioGroup}
          value={options.find(opt => opt.label === currentValue)?.id}
          itemsClassName={styles.radioItems}
          radioItemClassName={styles.drawerRadioItem}
          radioLabelClassName={styles.radioLabel}
        />
      )
    }

    // Format the label for the radio filter
    const formatRadioFilterLabel = () => {
      const singleSelectedValue =
        selectedCount === 1 && selectedOptions.length === 1 ? selectedOptions[0]?.label : undefined

      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }

    return (
      <DropdownV2
        key={filter.name}
        menu={{
          items: [
            {
              key: `${filter.name}-filter`,
              label: (
                <RadioButtonGroup
                  key={`radio-${selectedCount}`}
                  options={options.map(opt => ({
                    label: opt.label,
                    value: opt.id,
                    checked: opt.checked,
                  }))}
                  onChange={handleRadioChange}
                  name={filter.name}
                  className={styles.radioPadding}
                  value={options.find(opt => opt.label === currentValue)?.id}
                  itemsClassName={styles.radioItems}
                  radioItemClassName={styles.radioItem}
                  radioLabelClassName={styles.radioLabel}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatRadioFilterLabel()}</span>
      </DropdownV2>
    )
  }
  // Unified function to render filter component (for both inline and drawer)
  const renderFilterComponent = (filter: FilterItem, isDrawer = false, forceClose = false) => {
    const { config, name: filterName } = filter
    const filterType = config.type.toLowerCase()
    const state = selectedFiltersState[filterName] ?? []

    switch (filterType) {
      case FILTER_TYPES.CARD:
        return renderRadioFilter(
          filter,
          createCheckboxOptions(filter.values, state),
          selectedOptions => {
            handleCheckboxFilterChange(filter, selectedOptions)
          },
          isDrawer,
          forceClose,
        )

      case FILTER_TYPES.CHECKBOX:
        if (filterName === EFilterKeys.COLORS) {
          return renderColorFilter(filter, isDrawer, forceClose)
        }

        return renderCheckboxFilter(
          filter,
          createCheckboxOptions(filter.values, state),
          selectedOptions => {
            handleCheckboxFilterChange(filter, selectedOptions)
          },
          isDrawer,
          forceClose,
        )

      default:
        return null
    }
  }

  // Add this helper function inside the component
  const formatFilterLabel = (
    filterLabel: string,
    selectedCount: number,
    _singleSelectedValue?: string,
  ): React.ReactNode => {
    return (
      <>
        {toTitleCase(filterLabel)}
        {selectedCount > 0 && ` (${selectedCount})`}
      </>
    )
  }

  // Add a collapseChange handler
  const handleCollapseChange = (activeKeys: string[]) => {
    const newlyOpenedFilters = activeKeys.filter(key => !collapseActiveKeys.includes(key))
    newlyOpenedFilters.forEach(filterKey => {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    })

    setCollapseActiveKeys(activeKeys)
  }

  // Modify renderFilter to handle sticky state changes
  const renderFilter = (isIntersecting: boolean) => {
    // Close all dropdowns only on initial sticky state change
    if (isSticky !== !isIntersecting) {
      setIsSticky(!isIntersecting)
      if (!hasHandledStickyChange) {
        closeAllDropdowns()
        setHasHandledStickyChange(true)
      }
    }

    // Reset hasHandledStickyChange when going back to non-sticky
    if (isIntersecting && hasHandledStickyChange) {
      setHasHandledStickyChange(false)
    }

    return (
      <div
        data-testid="filters-container"
        className={classNames(styles.filtersContainer, className, {
          [`${styles.filtersContainerSticky}`]: !isIntersecting,
        })}
      >
        <div className={styles.filtersWrapper}>
          <div className={styles.filtersLeft}>
            <button
              className={classNames(
                styles.filterButton,
                effectiveTotalSelectedFilters > 0 && styles.filterButtonActive,
              )}
              onClick={handleMainFilterClick}
              data-testid={`filter-drawer-button-${effectiveTotalSelectedFilters}`}
            >
              <Icon iconSvg={FilterInlineIcon} className={styles.filterIcon} />
              {t('filter')} {effectiveTotalSelectedFilters > 0 && `(${effectiveTotalSelectedFilters})`}
            </button>

            {filters.map(filter => (
              <div key={filter.name} className={styles.filterItem}>
                {renderFilterComponent(filter, false, !isIntersecting && !hasHandledStickyChange)}
              </div>
            ))}

            {effectiveTotalSelectedFilters >= CLEAR_ALL_THRESHOLD && (
              <button className={styles.clearAllButton} onClick={clearAllFilters}>
                {t('clear_all_v1')}
              </button>
            )}
          </div>

          <div className={styles.filtersRight}>{renderSortDropdown()}</div>

          <FilterDrawer
            placement="left"
            open={isDrawerOpen}
            onClose={closeDrawer}
            selectedFilterChips={selectedFilterChips}
            onChipDismiss={handleChipDismissByLabel}
            clearAllFilters={clearAllFilters}
            totalSelectedFilters={effectiveTotalSelectedFilters}
            noOfItems={noOfItems}
            headerClassName={`${styles.drawerCustomHeader} filterDrawerExperimentHeader`}
            bodyClassName={`${styles.drawerBody} ${styles.noPaddingTop}`}
            isMobile={false}
          >
            <FilterCollapseGroup
              filters={filters}
              selectedFiltersState={selectedFiltersState}
              activeItems={collapseActiveKeys}
              onChange={handleCollapseChange}
              renderFilterComponent={renderFilterComponent}
              isMobile={false}
            />
          </FilterDrawer>
        </div>
      </div>
    )
  }

  if (showShimmer && !isDrawerOpen) {
    return <CollectionFilterLoader />
  }

  return (
    <div id="stickyWrapperFilterSectionWithDrawer" data-testid="stickyWrapperFilterSectionWithDrawer">
      <StickyWrapper renderChildren={renderFilter} stickyWrapperClassName={isDrawerOpen ? styles.stickyWrapper : ''} />
    </div>
  )
}
